/**
 * Generic skeleton building blocks with shimmering overlay
 */
import React from 'react';
import './Skeleton.css';

export const SkeletonBlock = ({ width = '100%', height = 12, className = '', style = {} }) => (
  <div
    className={`skeleton-line ${className}`}
    style={{ width, height, ...style }}
    aria-hidden="true"
  />
);

export const SkeletonCircle = ({ size = 40, className = '', style = {} }) => (
  <div
    className={`skeleton-circle ${className}`}
    style={{ width: size, height: size, ...style }}
    aria-hidden="true"
  />
);

export const SkeletonText = ({
  lines = 3,
  lineHeight = 12,
  gap = 10,
  widths = [],
  className = '',
}) => (
  <div className={`skeleton-text-stack ${className}`} aria-hidden="true">
    {Array.from({ length: lines }).map((_, idx) => {
      const width = widths[idx] || (idx === lines - 1 ? '72%' : '100%');
      const marginBottom = idx === lines - 1 ? 0 : gap;
      return (
        <SkeletonBlock
          key={idx}
          width={width}
          height={lineHeight}
          style={{ marginBottom }}
        />
      );
    })}
  </div>
);

export const SkeletonBadgeRow = ({ count = 2, width = 82, className = '' }) => (
  <div className={`skeleton-badge-row ${className}`} aria-hidden="true">
    {Array.from({ length: count }).map((_, idx) => (
      <SkeletonBlock
        key={idx}
        width={typeof width === 'number' ? `${width}px` : width}
        height={20}
        className="skeleton-pill"
      />
    ))}
  </div>
);

export const SkeletonImage = ({ height = '220px', radius = 12, className = '', style = {} }) => (
  <div
    className={`skeleton-image ${className}`}
    style={{ height, borderRadius: radius, ...style }}
    aria-hidden="true"
  >
    <div className="skeleton-pulse" style={{ width: '100%', height: '100%', borderRadius: radius }} />
  </div>
);

export const SkeletonRows = ({ rows = 3, columns = 3 }) => {
  const template = `repeat(${columns}, 1fr)`;
  return (
    <div className="skeleton-table" aria-hidden="true">
      {Array.from({ length: rows }).map((_, rowIdx) => (
        <div key={rowIdx} className="skeleton-table-row" style={{ gridTemplateColumns: template }}>
          {Array.from({ length: columns }).map((_, colIdx) => (
            <SkeletonBlock key={colIdx} height={14} />
          ))}
        </div>
      ))}
    </div>
  );
};

const Skeleton = {
  Block: SkeletonBlock,
  Circle: SkeletonCircle,
  Text: SkeletonText,
  BadgeRow: SkeletonBadgeRow,
  Image: SkeletonImage,
  Rows: SkeletonRows,
};

export default Skeleton;
